export interface SubRole {
  title: string
  dates: string
  location?: string
  summary: string
}

export interface Role {
  id: string
  title: string
  company: string
  employment?: string
  dates: string
  duration?: string
  location?: string
  summary: string
  bullets?: string[]
  subRoles?: SubRole[]
}

export const experience: Role[] = [
  {
    id: 'accelerate-learning',
    title: 'Senior Vice President, Product Management',
    company: 'STEMscopes by Accelerate Learning, Inc.',
    employment: 'Full-time',
    dates: 'Sep 2021 — Jun 2024',
    duration: '2 yrs 10 mos',
    location: 'Houston, Texas · Remote',
    summary:
      'Hired into a newly created role reporting to the CEO to build the Product Management organization, processes, and culture — spanning Product Managers, Product Owners, and User Experience — and to drive product transformation across SaaS, digital learning, AI, data, analytics, and customer experience.',
    bullets: [
      'Spearheaded the strategic transformation of the product ecosystem into a modern, AI-enabled and data-driven digital platform.',
      'Built Product Management processes, culture, and cross-functional operating models across Product, UX, Engineering, Sales, Marketing, and Customer Success.',
      'Established the company’s first formal product launch process, enabling new SaaS offerings and generating early six- and seven-figure revenue opportunities.',
      'Led cross-functional product planning and roadmap prioritization, improving alignment between customer needs, market opportunity, technology investment, and business objectives.',
      'Re-engineered product packaging and pricing strategies, increasing Average Selling Price while maintaining competitive positioning.',
      'Directed the enterprise data initiative, establishing a unified data lake and modern analytics framework to improve decision-making, renewal performance, and learning outcomes.',
      'Led customer focus groups and feedback initiatives to strengthen customer-centric development and product-market fit.',
      'Partnered with executive and financial stakeholders on the Confidential Information Memorandum supporting the company’s strategic sale process.',
      'Contributed to M&A due diligence and led post-acquisition integration for Finland-based Kide Science.',
    ],
  },
  {
    id: 'essential-pm',
    title: 'Founder and Principal',
    company: 'Essential Product Management LLC',
    employment: 'Contract',
    dates: 'Oct 2020 — Sep 2021',
    duration: '1 yr',
    location: 'Boston, Massachusetts',
    summary:
      'Founded and led an enterprise software and SaaS product management consulting practice supporting organizations across product strategy, portfolio management, go-to-market execution, customer engagement, and product operating models.',
    bullets: [
      'Advised organizations on product strategy, including build/buy/partner decisions and portfolio roadmaps.',
      'Developed go-to-market strategies spanning launches, pricing, packaging, positioning, and commercialization.',
      'Established Customer Advisory Board strategies creating continuous feedback loops and stronger product-market alignment.',
      'Designed and mentored cross-functional Product Management organizations, empowering PMs to operate as general managers of their product lines.',
      'Helped organizations establish scalable product management processes aligned to strategic objectives and commercial outcomes.',
    ],
  },
  {
    id: 'information-builders',
    title: 'Senior Vice President, Product Management',
    company: 'Information Builders',
    employment: 'Full-time',
    dates: 'Dec 2019 — Dec 2020',
    duration: '1 yr 1 mo',
    location: 'New York, New York · On-site',
    summary:
      'Hired into a newly created executive role reporting to the CEO to lead Product Management strategy and build the organization, processes, and culture.',
    bullets: [
      'Established a structured, cross-functional product planning process improving roadmap prioritization and organizational alignment.',
      'Directed Product Managers and Product Owners while implementing a strategic Cloud-First approach across the portfolio.',
      'Led the development and launch of AI/ML-powered analytics capabilities.',
      'Established the company’s first formal product launch process, generating early six-figure opportunities.',
      'Partnered with executive leadership, financial stakeholders, and investment banking teams on the Confidential Information Memorandum supporting the company’s strategic sale.',
      'Strengthened collaboration across Product, Engineering, Sales, Marketing, and executive leadership.',
    ],
  },
  {
    id: 'syncsort',
    title: 'Vice President, Product Management',
    company: 'Syncsort & Trillium Software',
    employment: 'Full-time',
    dates: 'Apr 2010 — Nov 2019',
    duration: '9 yrs 8 mos',
    location: 'Burlington, MA · Woodcliff Lake, NJ',
    summary:
      'Led global product strategy and product management across enterprise data, analytics, cloud, and adjacent markets during a period of significant organic and acquisition-driven growth in which the company tripled in size.',
    bullets: [
      'Defined the company’s acquisition product strategy and market adjacencies, overseeing technical due diligence contributing to eight worldwide acquisitions.',
      'Led pricing and packaging transformations that unlocked new revenue and contributed to multiple six-figure enterprise deals.',
      'Partnered with executive leadership and the Board to define forward-looking Big Data and cloud strategies.',
      'Led the launch of Hadoop-based products, generating more than $2M in bookings in the first year.',
      'Established a global Customer Advisory Board that contributed $2M in incremental revenue within a single quarter.',
      'Created and executed a cloud transformation strategy involving new products, launches, branding, and strategic partnership initiatives with Amazon.',
      'Identified high-growth investment segments and reprioritized the portfolio around the highest-value opportunities.',
    ],
    subRoles: [
      {
        title: 'Vice President, Product Management — Syncsort',
        dates: 'Dec 2016 — Oct 2018',
        location: 'Burlington, MA',
        summary:
          'Promoted following the acquisition of Trillium Software to lead global Product Management across the expanding enterprise software portfolio, directing roadmaps, go-to-market execution, and structured cross-functional planning.',
      },
      {
        title: 'Vice President, Product Management — Trillium Software',
        dates: 'Dec 2014 — Dec 2016',
        location: 'Burlington, MA',
        summary:
          'Hired to rebuild the product management discipline, processes, and team. Developed a three-year strategy reinforcing Trillium’s data quality leadership, established a global Customer Advisory Board, and served on the management team responsible for the carve-out sale from Harte Hanks — including CIM preparation, due diligence, and management presentations.',
      },
      {
        title: 'Director, Product Management — Syncsort',
        dates: 'Apr 2010 — Nov 2014',
        location: 'Boston, MA and Woodcliff Lake, NJ',
        summary:
          'Introduced product management discipline to Syncsort: strategy, roadmaps, cross-functional launches, simplified portfolio pricing and packaging, and extensive customer and partner engagement, achieving consistent recurring revenue growth.',
      },
    ],
  },
  {
    id: 'ibm-infosphere',
    title: 'Program Director, InfoSphere Product Management',
    company: 'IBM',
    employment: 'Full-time',
    dates: 'Apr 2005 — Apr 2010',
    duration: '5 yrs 1 mo',
    location: 'Littleton, MA · On-site',
    summary:
      'Joined through the acquisition of Ascential Software and promoted to lead product management for InfoSphere Information Server, with portfolio responsibility for revenue that grew from $270M to more than $600M.',
    bullets: [
      'Directed a global Product Management organization of 20+ geographically distributed Product Managers.',
      'Led enterprise portfolio strategy across data integration and information management solutions.',
      'Conducted worldwide executive-level customer engagements, translating requirements into product strategy.',
      'Led post-acquisition product integration strategies, aligning acquired technologies with the InfoSphere roadmap.',
      'Partnered with Sales, Engineering, Marketing, and executive stakeholders to prioritize investment around market opportunity and business objectives.',
    ],
  },
  {
    id: 'ascential',
    title: 'Director, Product Management',
    company: 'Ascential Software',
    employment: 'Full-time',
    dates: 'Nov 2001 — Apr 2005',
    duration: '3 yrs 6 mos',
    location: 'Westborough, MA · On-site',
    summary:
      'Led product management for the DataStage portfolio, driving growth through product innovation, market expansion, and customer-focused strategy.',
    bullets: [
      'Helped scale DataStage annual revenue from approximately $60M to more than $270M.',
      'Led product vision, roadmap development, lifecycle management, and cross-functional execution.',
      'Identified new market opportunities and translated customer and market requirements into product investments.',
      'Partnered with Engineering, Sales, and Marketing to accelerate launches and maximize commercial outcomes.',
      'Established data-driven product performance and investment frameworks supporting portfolio growth and profitability.',
    ],
  },
]

export interface EarlyRole {
  title: string
  company: string
  dates: string
  location?: string
  summary: string
}

export const earlyCareer: EarlyRole[] = [
  {
    title: 'Product Manager, Orchestrate',
    company: 'Torrent Systems',
    dates: 'Jun 2001 — Nov 2001',
    location: 'Cambridge, MA',
    summary:
      'Product Manager for Orchestrate, the high-performance parallel engine, with $5M in revenue.',
  },
  {
    title: 'Director, Product Management',
    company: 'PFN Inc',
    dates: '2000 — 2001',
    location: 'Cambridge, MA',
    summary:
      'Product management and product marketing for PFN’s entry into managed network services.',
  },
  {
    title: 'Product and Marketing Manager',
    company: 'Micro Focus',
    dates: '1998 — 2000',
    location: 'Westchester, IL',
    summary:
      'Product and international marketing manager for application transformation products and solutions, including application analysis (COBOL, CICS, IMS). Grew 15,000 user licenses and maintenance revenue by over 20%.',
  },
  {
    title: 'Product Manager, Middleware',
    company: 'Seer Technologies',
    dates: '1995 — 1998',
    location: 'Cary, NC',
    summary: 'Managed Seer’s entry into the middleware EAI marketplace.',
  },
  {
    title: 'Senior Sales Engineer',
    company: 'Seer Technologies',
    dates: '1994 — 1995',
    location: 'Chicago, IL',
    summary: 'Technical sales for middleware and enterprise application development tools.',
  },
  {
    title: 'Market Manager',
    company: 'IBM',
    dates: '1992 — 1994',
    location: 'Cary, NC',
    summary:
      'Prepared and implemented the marketing plan for ISPF, exceeding the 1993 full-year revenue plan by May with over $250M.',
  },
  {
    title: 'Senior Associate Programmer',
    company: 'IBM',
    dates: '1988 — 1992',
    location: 'Cary, NC',
    summary:
      'Designed, developed, and maintained multiple functional components of ISPF/SCLM mainframe software.',
  },
]
